/* ACG Trefl USA — component behaviour.
 *
 * Three small things, no framework, no dependencies:
 *   1. hero carousel
 *   2. favourite heart (this browser only)
 *   3. add-to-cart without a page reload
 *
 * Everything degrades: with the script blocked the hero shows its first slide,
 * the heart is simply absent from the markup unless enabled, and the Add
 * button falls back to a normal form POST to /cart/add.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- hero */

  function initHero(root) {
    var slides = root.querySelectorAll('.tf-hero__slide');
    if (slides.length < 2) return;

    var dots = root.querySelector('[data-tf-hero-dots]');
    var i = 0;
    var timer = null;

    function show(n) {
      slides[i].classList.remove('is-on');
      if (dots) dots.children[i].classList.remove('is-on');
      i = (n + slides.length) % slides.length;
      slides[i].classList.add('is-on');
      if (dots) dots.children[i].classList.add('is-on');
    }

    if (dots) {
      Array.prototype.forEach.call(slides, function (s, x) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', 'Slide ' + (x + 1));
        if (x === 0) b.className = 'is-on';
        b.addEventListener('click', function () { show(x); restart(); });
        dots.appendChild(b);
      });
    }

    var prev = root.querySelector('[data-tf-hero-prev]');
    var next = root.querySelector('[data-tf-hero-next]');
    if (prev) prev.addEventListener('click', function () { show(i - 1); restart(); });
    if (next) next.addEventListener('click', function () { show(i + 1); restart(); });

    /* Touch swipe.

       The arrows are hidden below 1000px, so before this the dots were the
       only way to change slide on a phone, and a carousel that does not
       respond to a swipe reads as broken.

       Both listeners are passive and nothing calls preventDefault, so
       vertical page scrolling is never blocked. The axis test is what keeps
       the two apart: a gesture only counts as a swipe when it is clearly
       horizontal, otherwise someone scrolling the page past the hero would
       skip slides by accident. */
    var swipeX = 0;
    var swipeY = 0;
    var swiping = false;

    root.addEventListener('touchstart', function (e) {
      // Ignore pinch and other multi-touch gestures.
      if (e.touches.length !== 1) { swiping = false; return; }
      swipeX = e.touches[0].clientX;
      swipeY = e.touches[0].clientY;
      swiping = true;
      // Stop autoplay advancing the slide out from under a finger.
      if (timer) clearInterval(timer);
    }, { passive: true });

    root.addEventListener('touchend', function (e) {
      if (!swiping) return;
      swiping = false;

      var touch = e.changedTouches[0];
      var dx = touch.clientX - swipeX;
      var dy = touch.clientY - swipeY;

      // 40px so a tap with a little travel is not read as a swipe, and
      // 1.5x so the intent has to be plainly horizontal.
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        show(dx < 0 ? i + 1 : i - 1);
      }

      restart();
    }, { passive: true });

    root.addEventListener('touchcancel', function () {
      swiping = false;
      restart();
    }, { passive: true });

    function restart() {
      if (timer) clearInterval(timer);
      start();
    }

    function start() {
      // Autoplaying a carousel under reduced-motion is the exact thing the
      // preference is asking us not to do.
      if (reduceMotion) return;
      if (root.dataset.autoplay !== 'true') return;
      var secs = parseInt(root.dataset.speed, 10) || 6;
      timer = setInterval(function () { show(i + 1); }, secs * 1000);
    }

    root.addEventListener('mouseenter', function () { if (timer) clearInterval(timer); });
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', function () { if (timer) clearInterval(timer); });

    start();
  }

  /* ----------------------------------------------------------- favourites */

  var FAV_KEY = 'trefl:favourites';

  function readFavs() {
    try {
      return JSON.parse(window.localStorage.getItem(FAV_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function writeFavs(list) {
    try {
      window.localStorage.setItem(FAV_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode or storage full — the heart just stops persisting */
    }
  }

  function initFavs() {
    var favs = readFavs();

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tf-fav]');
      if (!btn) return;
      e.preventDefault();

      var id = btn.getAttribute('data-tf-fav');
      var on = btn.getAttribute('aria-pressed') === 'true';
      var next = !on;

      btn.setAttribute('aria-pressed', String(next));

      favs = readFavs();
      var at = favs.indexOf(id);
      if (next && at === -1) favs.push(id);
      if (!next && at !== -1) favs.splice(at, 1);
      writeFavs(favs);
    });

    // Paint saved state on load.
    document.querySelectorAll('[data-tf-fav]').forEach(function (btn) {
      if (favs.indexOf(btn.getAttribute('data-tf-fav')) !== -1) {
        btn.setAttribute('aria-pressed', 'true');
      }
    });
  }

  /* ------------------------------------------------------------ add to cart */

  function initAdd() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-tf-add]');
      if (!form) return;

      e.preventDefault();
      var btn = form.querySelector('button[type="submit"]');
      var original = btn ? btn.textContent : '';
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Adding…';
      }

      fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/javascript' }
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body.description || 'Could not add');
          if (btn) btn.textContent = 'Added ✓';
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'trefl-card' } }));
        })
        .catch(function (err) {
          if (btn) btn.textContent = 'Try again';
          if (window.console) console.warn('[trefl] add to cart failed:', err.message);
        })
        .finally(function () {
          setTimeout(function () {
            if (btn) {
              btn.disabled = false;
              btn.textContent = original;
            }
          }, 1600);
        });
    });
  }

  /* -------------------------------------------------------------- pdp gallery */

  // The gallery is a scroll-snap track. Everything here drives the SCROLL
  // POSITION and then reads it back to update state, rather than keeping a
  // separate "current index" variable. A swipe, an arrow click, a thumbnail
  // and a keyboard press all end up in the same place, so the counter and the
  // active thumbnail cannot disagree with what is actually on screen.
  function initGallery() {
    document.querySelectorAll('[data-tf-gallery]').forEach(function (gal) {
      var track = gal.querySelector('[data-tf-gal-track]');
      if (!track) return;

      var slides = Array.prototype.slice.call(gal.querySelectorAll('[data-tf-gal-slide]'));
      var thumbs = Array.prototype.slice.call(gal.querySelectorAll('[data-tf-thumb]'));
      var count = gal.querySelector('[data-tf-gal-count]');
      var prev = gal.querySelector('[data-tf-gal-prev]');
      var next = gal.querySelector('[data-tf-gal-next]');
      if (slides.length < 2) return;

      function current() {
        // Nearest slide to the track's left edge. Reading geometry beats
        // tracking an index, because a flick-scroll can land anywhere.
        var x = track.scrollLeft;
        var best = 0;
        var bestD = Infinity;
        slides.forEach(function (s, i) {
          var d = Math.abs(s.offsetLeft - track.offsetLeft - x);
          if (d < bestD) { bestD = d; best = i; }
        });
        return best;
      }

      // Move the track by assigning scrollLeft, synchronously, in one go.
      //
      // The obvious alternatives were both tried and both rejected. CSS
      // scroll-behavior: smooth and scrollTo({behavior:'smooth'}) are animated
      // by the browser frame by frame, and on a scroll-snap-type: mandatory
      // container the snap re-runs between frames and drags the track back to
      // where it started. A hand-rolled requestAnimationFrame tween loses to
      // the same race. A single synchronous assignment happens inside one task,
      // before snapping gets a look in, so it always lands.
      //
      // The cost is that paging is instant rather than eased. That is a normal
      // carousel behaviour and worth it for something that cannot silently do
      // nothing.
      function goTo(i) {
        var n = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollLeft = slides[n].offsetLeft - track.offsetLeft;
        sync();
      }

      function sync() {
        var i = current();
        thumbs.forEach(function (t, n) { t.classList.toggle('is-on', n === i); });
        if (count) count.textContent = (i + 1) + ' / ' + slides.length;
        if (prev) prev.disabled = i === 0;
        if (next) next.disabled = i === slides.length - 1;

        // Keep the active thumbnail in view when the strip overflows. Same
        // reasoning as goTo: assign the scroll position rather than asking for
        // a smooth scroll that may never be honoured.
        var on = thumbs[i];
        var strip = on && on.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          var left = on.offsetLeft - strip.offsetLeft;
          var right = left + on.offsetWidth;
          if (left < strip.scrollLeft) {
            strip.scrollLeft = left - 8;
          } else if (right > strip.scrollLeft + strip.clientWidth) {
            strip.scrollLeft = right - strip.clientWidth + 8;
          }
        }
      }

      var t = null;
      track.addEventListener('scroll', function () {
        clearTimeout(t);
        t = setTimeout(sync, 60);
      }, { passive: true });

      if (prev) prev.addEventListener('click', function () { goTo(current() - 1); });
      if (next) next.addEventListener('click', function () { goTo(current() + 1); });

      thumbs.forEach(function (th, i) {
        th.addEventListener('click', function () { goTo(i); });
      });

      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current() - 1); }
      });

      sync();
    });
  }

  /* ------------------------------------------------------------ qty stepper */

  function initQty() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tf-qty]');
      if (!btn) return;
      e.preventDefault();

      var wrap = btn.closest('.tf-qty');
      var input = wrap && wrap.querySelector('input');
      if (!input) return;

      var min = parseInt(input.min, 10) || 1;
      var step = parseInt(input.step, 10) || 1;
      var val = parseInt(input.value, 10) || min;

      val = btn.getAttribute('data-tf-qty') === 'up' ? val + step : val - step;
      input.value = Math.max(min, val);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }


  /* ---------------------------------------------------------- mega nav */

  function initMega() {
    var nav = document.querySelector('[data-tf-mega]');
    if (!nav) return;

    var items = nav.querySelectorAll('.tf-mega__item--has-panel');
    var openTimer = null;
    var closeTimer = null;

    function close(item) {
      var t = item.querySelector('[data-tf-mega-trigger]');
      var p = item.querySelector('[data-tf-mega-panel]');
      if (!t || !p) return;
      t.setAttribute('aria-expanded', 'false');
      p.hidden = true;
    }

    function closeAll(except) {
      items.forEach(function (i) { if (i !== except) close(i); });
    }

    function open(item) {
      closeAll(item);
      var t = item.querySelector('[data-tf-mega-trigger]');
      var p = item.querySelector('[data-tf-mega-panel]');
      if (!t || !p) return;
      t.setAttribute('aria-expanded', 'true');
      p.hidden = false;
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-tf-mega-trigger]');

      // Click is the primary interaction so the menu works on touch and for
      // keyboard users; hover is layered on top for pointer users.
      //
      // Exception: a trigger that is a real link (an item with no children,
      // e.g. Contact) owns its click. Swallowing it to toggle a panel would
      // strand touch users on a page they cannot reach, since they have no
      // hover to open the panel with in the first place.
      if (!trigger.hasAttribute('data-tf-mega-link')) {
        trigger.addEventListener('click', function () {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) { close(item); } else { open(item); }
        });
      }

      item.addEventListener('mouseenter', function () {
        if (!window.matchMedia('(hover: hover)').matches) return;
        clearTimeout(closeTimer);
        openTimer = setTimeout(function () { open(item); }, 90);
      });

      item.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
        closeTimer = setTimeout(function () { close(item); }, 180);
      });

      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) close(item);
      });
    });

    nav.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeAll(null);
      var open_ = nav.querySelector('[aria-expanded="true"]');
      if (open_) open_.focus();
    });

    document.addEventListener('click', function (e) {
      if (!nav.contains(e.target)) closeAll(null);
    });
  }

  /* -------------------------------------------------- collection filters */

  function initFilters() {
    var form = document.querySelector('[data-tf-filters]');
    if (!form) return;

    // Mark panels live so their Apply buttons hide; without JS they stay.
    form.querySelectorAll('.tf-drop__panel').forEach(function (p) {
      p.classList.add('js-live');
    });

    var debounce = null;

    form.addEventListener('change', function (e) {
      if (!e.target.matches('input, select')) return;
      clearTimeout(debounce);
      // Price is typed, so give it a beat before navigating.
      var wait = e.target.type === 'number' ? 600 : 0;
      debounce = setTimeout(function () { form.requestSubmit(); }, wait);
    });

    // Only one dropdown open at a time.
    form.querySelectorAll('details.tf-drop').forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        form.querySelectorAll('details.tf-drop').forEach(function (o) {
          if (o !== d) o.open = false;
        });
      });
    });

    document.addEventListener('click', function (e) {
      if (form.contains(e.target)) return;
      form.querySelectorAll('details.tf-drop[open]').forEach(function (d) { d.open = false; });
    });
  }


  /* ------------------------------------------------- wholesale quick order */

  function initQuickOrder() {
    var form = document.querySelector('[data-tf-qo]');
    if (!form) return;

    var rows = Array.prototype.slice.call(form.querySelectorAll('[data-tf-qo-row]'));
    var elLines = form.querySelector('[data-tf-qo-lines]');
    var elUnits = form.querySelector('[data-tf-qo-units]');
    var elTotal = form.querySelector('[data-tf-qo-total]');
    var elShown = form.querySelector('[data-tf-qo-shown]');
    var submit = form.querySelector('[data-tf-qo-submit]');
    var filter = form.querySelector('[data-tf-qo-filter]');

    // Read the money format off the server-rendered zero total so the running
    // total matches the store's currency formatting exactly, rather than
    // hard-coding a dollar sign here.
    var zero = elTotal ? elTotal.textContent.trim() : '$0.00';
    var prefix = zero.replace(/[\d.,\s]+$/, '');
    var suffix = '';
    var m = zero.match(/([^\d.,]+)$/);
    if (m && zero.indexOf(m[1]) > 0) suffix = m[1];

    function money(cents) {
      var v = (cents / 100).toFixed(2);
      var parts = v.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return prefix + parts.join('.') + suffix;
    }

    function recalc() {
      var lines = 0, units = 0, total = 0;

      rows.forEach(function (row) {
        var input = row.querySelector('[data-tf-qo-qty]');
        var lineEl = row.querySelector('[data-tf-qo-line]');
        if (!input) return;

        var qty = parseInt(input.value, 10) || 0;
        var price = parseInt(row.getAttribute('data-price'), 10) || 0;
        var step = parseInt(row.getAttribute('data-case'), 10) || 1;

        // Snap up to the next whole case. A buyer cannot take 7 of a 10-pack.
        if (qty > 0 && step > 1 && qty % step !== 0) {
          qty = Math.ceil(qty / step) * step;
          input.value = qty;
        }

        if (qty > 0) {
          lines += 1;
          units += qty;
          total += qty * price;
          row.classList.add('is-active');
          if (lineEl) lineEl.textContent = money(qty * price);
        } else {
          row.classList.remove('is-active');
          if (lineEl) lineEl.textContent = '—';
        }
      });

      if (elLines) elLines.textContent = lines;
      if (elUnits) elUnits.textContent = units;
      if (elTotal) elTotal.textContent = money(total);
      if (submit) submit.disabled = lines === 0;
    }

    form.addEventListener('input', function (e) {
      if (e.target.matches('[data-tf-qo-qty]')) recalc();
    });
    form.addEventListener('change', function (e) {
      if (e.target.matches('[data-tf-qo-qty]')) recalc();
    });

    if (filter) {
      var t = null;
      filter.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          var q = filter.value.trim().toLowerCase();
          var shown = 0;
          rows.forEach(function (row) {
            var hit = q === '' ||
              (row.getAttribute('data-title') || '').indexOf(q) !== -1 ||
              (row.getAttribute('data-sku') || '').indexOf(q) !== -1;
            row.classList.toggle('is-hidden', !hit);
            if (hit) shown += 1;
          });
          if (elShown) elShown.textContent = shown + (shown === 1 ? ' item' : ' items');
        }, 120);
      });
    }

    // Zero-quantity lines must not be posted; Shopify rejects the whole cart
    // add if any line is 0.
    form.addEventListener('submit', function () {
      rows.forEach(function (row) {
        var input = row.querySelector('[data-tf-qo-qty]');
        if (!input) return;
        var qty = parseInt(input.value, 10) || 0;
        if (qty > 0) return;
        input.disabled = true;
        var id = row.querySelector('input[type="hidden"]');
        if (id) id.disabled = true;
      });
    });

    recalc();
  }

  /* --------------------------------------------------------------- boot */

  function boot() {
    document.querySelectorAll('[data-tf-hero]').forEach(initHero);
    initFavs();
    initAdd();
    initGallery();
    initQty();
    initMega();
    initFilters();
    initQuickOrder();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind hero instances the theme editor injects after load.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-tf-hero]').forEach(initHero);
  });
})();
